"use client";

import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

interface StudioHeroSlideshowProps {
  images: string[];
  alt: string;
}

const INTERVAL_MS = 3000;

// Animated hero for the studio detail pages (revision 1.1.2 item 3). Crossfades
// through the studio's gallery every 3s. With a single image it renders a plain
// static photo — no timer, no dots. Respects prefers-reduced-motion and pauses
// while the tab is hidden. Container styling matches the old static hero exactly
// so it drops into the page grid without any layout shift.
export const StudioHeroSlideshow: React.FC<StudioHeroSlideshowProps> = ({
  images,
  alt,
}) => {
  const slides = images.filter(Boolean);
  const isSlideshow = slides.length > 1;

  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isSlideshow) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const start = () => {
      stop();
      timerRef.current = setInterval(() => {
        setIndex((i) => (i + 1) % slides.length);
      }, INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // Restart the timer if the slide set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlideshow, slides.length]);

  const goTo = (i: number) => {
    setIndex(i);
    // Reset the 3s timer so a manual jump gets a full dwell.
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setIndex((prev) => (prev + 1) % slides.length);
      }, INTERVAL_MS);
    }
  };

  return (
    <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/3] bg-surface-container-high border border-white/60">
      {isSlideshow ? (
        <AnimatePresence initial={false} mode="sync">
          <motion.img
            key={index}
            src={slides[index]}
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={slides[0]}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {isSlideshow && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === index
                  ? "w-6 bg-white"
                  : "w-2 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
